import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Select2ScrollEvent, Select2SearchEvent, Select2UpdateEvent } from 'ng-select2-component';
import { APP } from 'src/app/config/app';
import { VehiclesService } from 'src/app/services/vehicles/vehicles.service';

const SEARCH_DEBOUNCE_MILLISECONDS = 500;

@Component({
  selector: 'app-vehicle-select',
  templateUrl: './vehicle-select.component.html',
  standalone: false
})
export class VehicleSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() clientId?: number;
  @Input() value?: any;
  // Vehicles that must stay selectable regardless of the loaded page: the assigned one, or one just created
  @Input() extraOptions: { value: any; label: string }[] = [];
  @Input() placeholder = '';
  @Input() overlay = false;
  @Input() disabled = false;
  @Output() vehicleSelected = new EventEmitter<{ value: any; label: string }>();

  public vehicles: { value: any; label: string }[] = [];

  private page = 1;
  private lastPage = false;
  private searchValue?: string;
  private searchTimeout?: ReturnType<typeof setTimeout>;
  private latestRequest = 0;

  constructor(private vehiclesService: VehiclesService) {}

  ngOnInit() {
    return this.reload();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['clientId'] && !changes['clientId'].firstChange) this.reload();
    else if (changes['extraOptions'] && !changes['extraOptions'].firstChange) {
      this.vehicles = this.withExtraOptions(this.vehicles);
    }
  }

  ngOnDestroy() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  }

  onUpdate(event: Select2UpdateEvent<any> | any) {
    const value = event?.value ?? event;
    const label = this.vehicles.find((vehicle) => vehicle.value === value)?.label ?? '';
    this.vehicleSelected.emit({ value, label });
  }

  async onScroll(event: Select2ScrollEvent) {
    if (event.way !== 'down' || this.lastPage) return;
    this.page += 1;
    await this.load(true);
  }

  onSearch(event: Select2SearchEvent) {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.searchValue = event.search || undefined;
      this.reload();
    }, SEARCH_DEBOUNCE_MILLISECONDS);
  }

  private reload() {
    this.page = 1;
    this.lastPage = false;
    return this.load();
  }

  private async load(append = false) {
    const request = ++this.latestRequest;
    try {
      const vehiclesPage: any = await this.vehiclesService.filterVehicles(
        this.page,
        APP.DEFAULT_PAGE_SIZE,
        this.clientId ? [this.clientId] : [],
        [],
        true,
        this.searchValue,
      );
      // A slower earlier request must not overwrite the list of a newer one
      if (request !== this.latestRequest) return;
      const loadedVehicles = (vehiclesPage?.content ?? []).map((vehicle: any) => ({
        value: vehicle.id,
        label: vehicle.name,
      }));
      this.lastPage = !!vehiclesPage?.last;
      this.vehicles = this.withExtraOptions(append ? [...this.vehicles, ...loadedVehicles] : loadedVehicles);
    } catch (error) {
      console.error('Could not load the vehicles', error);
      if (request !== this.latestRequest) return;
      if (!append) this.vehicles = [];
      this.lastPage = true;
    }
  }

  private withExtraOptions(loadedVehicles: { value: any; label: string }[]) {
    const missing = this.extraOptions.filter(
      (option) => option?.value && !loadedVehicles.some((vehicle) => vehicle.value === option.value),
    );
    return missing.length > 0 ? [...loadedVehicles, ...missing] : loadedVehicles;
  }
}
